import { MASTER_PAYMENT_STATUS } from "constants/paymentStatus";
import { CLIENT_URLS } from "constants/urls";
import { doAppendQueryParam } from "../helpers/index";
import { doRedirect } from "helpers/utils/redirection";
import { toLowerCase } from "helpers/utils/toLowerCase";
import { getPaymentFailurePollingDuration } from "layouts/Payment/common/dependencies/dependencyhelpers";
import { useContext, useRef, useState, useEffect } from "react";
import { getOrdersPaymentResponse as getPaymentResponseApi } from "store/payment/services";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";

import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { initialPaymentInfoState } from "wrapper/PaymentWrapper/dependencies/constants";
import { useHistory } from "react-router-dom";
import { isServer } from "helpers/utils/isServer";

/**
 * @description -- function to calculate whether payment failure bottomsheet
 *  should be shown to the user or not on the basis of masterPaymentStatus and showFallbackModes
 * it handles all the logic of showing lottie, polling handling and page refresh handling in web
 * @params - history
 * @returns - showFallbackModeSheet, setShowFallbackModeSheet
 */
export const usePaymentFailureConfig = () => {
  const { paymentResponseV2Data, setPaymentResponseV2Data, isPaymentInfoLoading, paymentInfo, isGpayRazorpayIntentDetailsLoading, setLoading } = useContext(
    PaymentContext
  );
  const { NewPaymentFailure, AutoOrder } = useContext(ExperimentAndSwitchContext);
  const failureFlowEnabled = NewPaymentFailure?.switchEnabled && NewPaymentFailure?.experimentEnabled;
  const paymentInfoFetched =
    !isPaymentInfoLoading && Object.keys(paymentInfo)?.length !== Object.keys(initialPaymentInfoState)?.length;
  const pollTime = paymentInfo?.paymentConversionPollTime || 10;
  const pollingDuration = getPaymentFailurePollingDuration();
  const [showFallbackModeSheet, setShowFallbackModeSheet] = useState(false);
  const [pollTimeLeft, setPollTimeLeft] = useState(-1);
  const pollTimerRef: { current: number | undefined | NodeJS.Timeout } = useRef();
  const history = useHistory();
  /**
   * New payment failure flow data
   */
  const masterPaymentStatus = paymentResponseV2Data?.masterPaymentStatus;
  let showFallbackModes = paymentResponseV2Data?.showFallbackModes;
  /* eslint-disable @typescript-eslint/no-use-before-define */
  let prevOrderId = paymentResponseV2Data?.orderId;

  /**
   * On Page Refresh, orderId is not present in paymentResponseV2Data but it will be
   * there in queryparams & failureFlowEnabled, hit the payment/response/v2 API
   */
  useEffect(() => {
     const query = new URLSearchParams(history.location.search);
     let failureOrderId = query.get("paymentFailureOrderId") || "";
      if (failureOrderId && failureFlowEnabled) {
        /* eslint-disable react-hooks/exhaustive-deps */
        if (!prevOrderId ) {
        paymentResponseHandler(failureOrderId, true);
        prevOrderId = failureOrderId;
        }
      }
  }, []);

  /**
   * hook to set the polling time on the basis of master payment status
   */
  useEffect(() => {
    if (
      paymentInfoFetched &&
      showFallbackModes &&
      masterPaymentStatus === MASTER_PAYMENT_STATUS.PENDING
    ) {
      const date = new Date();
      // added for testing
      console.log("------Polling Started-----", `${date.getMinutes()}: ${date.getSeconds()}`);
      // manipulating polling time
      setPollTimeLeft(pollTime > 5 ? (pollTime - 5) * 1000 : pollTime * 1000); //pollTime  seconds loader and poll paymentResponse in every 2 seconds
      // paymentResponseHandler(prevOrderId, false); -- no loader pending issue fix
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [paymentInfoFetched, masterPaymentStatus]);

  /**
   * hook to poll the payment/response/v2 API
   */
  useEffect(() => {
    if (showFallbackModes) {
      /**
       * After getInfoForPaymentCreation Api has given success and razorpaysdk has given gPay intent visibility status,
       * if any of either polling completed(i.e pollTimeLeft is 0) or masterPaymentStatus is FAILED or USER_DROPOUT
       * open the payment failure bottomsheet
       */
      if (paymentInfoFetched && !isGpayRazorpayIntentDetailsLoading && (pollTimeLeft === 0 || (masterPaymentStatus === MASTER_PAYMENT_STATUS.FAILURE ||
        masterPaymentStatus === MASTER_PAYMENT_STATUS.USER_DROPPED))) {
        clearInterval(pollTimerRef?.current);
        // Atleast 1 Lottie iteration should be there in case of payment failure
        if (masterPaymentStatus === MASTER_PAYMENT_STATUS.FAILURE || masterPaymentStatus === MASTER_PAYMENT_STATUS.USER_DROPPED) {
          setTimeout(() => {
            setShowFallbackModeSheet(true);
            if (!isServer() && window && !window.location?.href?.includes("showFallbackBottomSheet")) {
              let url = doAppendQueryParam("showFallbackBottomSheet", "true");
              history.replace(url);
            }
          }, 2000);
        } else if (pollTimeLeft === 0) {
          setShowFallbackModeSheet(true);
          if (!isServer() && window && !window.location?.href?.includes("showFallbackBottomSheet")) {
            let url = doAppendQueryParam("showFallbackBottomSheet", "true");
            history.replace(url);
          }
        }
      } else if (masterPaymentStatus === MASTER_PAYMENT_STATUS.PENDING) {
        if (pollTimeLeft > 0) {
          pollTimerRef.current = setInterval(() => {
            paymentResponseHandler(prevOrderId, false);
          }, pollingDuration);
        }
      }
    }
    return () => clearInterval(pollTimerRef?.current);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pollTimeLeft, isGpayRazorpayIntentDetailsLoading, paymentInfoFetched]);

  /**
   * @description -- function to hit the payment/response/v2 API and update state
   */
  const paymentResponseHandler = async (orderId: string, queryParamsHit = false) => {
    const params = {
      orderId: orderId,
    };
    let paymentResponseData = await getPaymentResponseApi(params);
    if (paymentResponseData?.paymentStatus) {
      const paymentStatus = paymentResponseData?.paymentStatus;
      const currMasterPaymentStatus = paymentResponseData.masterPaymentStatus;
      /* eslint-disable no-shadow */
      const showFallbackModes = paymentResponseData.showFallbackModes;
      const paymentMode = paymentResponseData?.paymentMode;
      const hashValue = paymentResponseData?.hashValue;
      const walletAmountUsed = paymentResponseData?.walletAmountUsed;
      const autoOrderEligible = paymentResponseData?.autoOrderEligible;

      //updating the payment state only when  master payment status is changing
      if (currMasterPaymentStatus && currMasterPaymentStatus !== masterPaymentStatus) {
        setPaymentResponseV2Data({
          ...paymentResponseV2Data,
          paymentStatus,
          masterPaymentStatus: currMasterPaymentStatus,
          showFallbackModes,
          paymentMode: toLowerCase(paymentMode || ""),
          orderId,
          walletAmountUsed,
        });
      }
      handlePaymentStatusChange(
        currMasterPaymentStatus,
        orderId,
        hashValue,
        queryParamsHit,
        autoOrderEligible
      );
    }
     else {
      //hitting the API again if payment response v2 is failing
      setPollTimeLeft(pollTimeLeft - pollingDuration);
      paymentResponseHandler(prevOrderId, queryParamsHit);
    }
  };

  /**
   * @description -- function to handle redirections and bottomsheet after
   * payment/response/v2 API
   */

  const handlePaymentStatusChange = (
    masterPaymentStatus: string,
    orderId: string,
    hashValue: string,
    queryParamsHit: boolean,
    autoOrderEligible: boolean,
  ) => {
    if (masterPaymentStatus == MASTER_PAYMENT_STATUS.SUCCESS) {
      setPollTimeLeft(0);
      clearInterval(pollTimerRef?.current);
      setLoading(false);
      // Redirecting to postpayment for further redirections
      let redirectionUrl = `${CLIENT_URLS.POST_PAYMENT_PAGE}?orderId=${orderId}&isOrderSuccess=true`;
      if (hashValue) {
        redirectionUrl = `${redirectionUrl}&hashvalue=${hashValue}`;
      }
      doRedirect({
        url: redirectionUrl,
        isClientRedirection: true,
        history,
      });
    } else if (
      masterPaymentStatus == MASTER_PAYMENT_STATUS.FAILURE ||
      masterPaymentStatus == MASTER_PAYMENT_STATUS.USER_DROPPED
    ) {
      /** if auto Orders is true redirect to auto-orders screen */
      if(autoOrderEligible && AutoOrder.experimentEnabled){
        history.push(`${CLIENT_URLS.AUTO_ORDERS}?orderId=${orderId}`);
      }      
      setPollTimeLeft(0);
    } else if (masterPaymentStatus == MASTER_PAYMENT_STATUS.PENDING) {      

      if (queryParamsHit) {
        // set poll time left if API is hitted first time and status is pending
        setPollTimeLeft(pollTime * 1000);
      } else if (pollTimeLeft > pollingDuration) {
        setPollTimeLeft(pollTimeLeft - pollingDuration);
      } else {
        // stop the polling if polltime has been passed but status is still pending
        setPollTimeLeft(0);
        /** if auto Orders is true redirect to auto-orders screen */
        if(autoOrderEligible && AutoOrder.experimentEnabled){
          history.push(`${CLIENT_URLS.AUTO_ORDERS}?orderId=${orderId}`);
        }

      }
    }
  };
  // remove show lottie state later from after check
  return { showFallbackModeSheet, setShowFallbackModeSheet, showPollingScreen: pollTimeLeft > 0 };
};
